import { z } from 'zod';
import type {
  WidthLabel,
  HeightLabel,
  WidgetFilterGroup,
  WidgetType,
  DashboardScopeType,
} from './types';

// ─── Filters (FilterGroup serialized into query.filtersJson) ──────────────────
// The recursive FilterGroup shape is validated structurally by the FilterBuilder
// UI (which only emits valid fields/operators), so the form holds it as an opaque
// value. A typed Zod schema here would diverge input/output types and break the
// react-hook-form Control typing.
const filtersSchema = z.custom<WidgetFilterGroup>();

// ─── Layout ──────────────────────────────────────────────────────────────────

export const widgetLayoutBreakpointSchema = z.object({
  x: z.number().min(0),
  y: z.number().min(0),
  w: z.union([z.literal(3), z.literal(4), z.literal(6), z.literal(8), z.literal(12)]),
  h: z.union([z.literal(2), z.literal(3), z.literal(4), z.literal(6)]),
});

export const widgetLayoutSchema = z.object({
  desktop: widgetLayoutBreakpointSchema,
  tablet: widgetLayoutBreakpointSchema,
  mobile: widgetLayoutBreakpointSchema,
});

// ─── GroupByDimension per widget type ─────────────────────────────────────────
// Matches the backend's Widget.InvalidGroupByForType rule: Day/Week only apply
// to Line/Area, every other dimension only applies to the series-shaped types,
// and Counter/Table have no groupBy at all (single source of truth — also used
// by StepConfig to build the "group by" dropdown).

export const METRICS_BY_TYPE: Record<
  WidgetType,
  Array<'Assignee' | 'Status' | 'Priority' | 'Project' | 'Board' | 'Group' | 'Day' | 'Week'>
> = {
  counter: [],
  pie: ['Assignee', 'Status', 'Priority', 'Project', 'Board', 'Group'],
  donut: ['Assignee', 'Status', 'Priority', 'Project', 'Board', 'Group'],
  bar: ['Assignee', 'Status', 'Priority', 'Project', 'Board', 'Group'],
  horizontal_bar: ['Assignee', 'Status', 'Priority', 'Project', 'Board', 'Group'],
  stacked_bar: ['Assignee', 'Status', 'Priority', 'Project', 'Board', 'Group'],
  line: ['Day', 'Week'],
  area: ['Day', 'Week'],
  table: [],
};

// Widget.CrossScopeReference: groupBy=Project is only valid on the tenant/global
// dashboard AND only when the widget itself isn't pinned to a project — the
// backend rejects it in both cases (a single project collapses to one bucket).
export function getAvailableMetrics(
  type: WidgetType,
  scopeType: DashboardScopeType,
  hasPinnedProject = false,
): string[] {
  const projectScoped = scopeType === 'project' || scopeType === 'board' || hasPinnedProject;
  return METRICS_BY_TYPE[type].filter((m) => !(m === 'Project' && projectScoped));
}

// Aggregation applies to every shape except Table (fixed columns, no computed
// value). Counter uses it directly as the scalar; series/timeseries types use
// it as the per-bucket value (defaults to Count, matching prior behavior).
export function typeSupportsAggregation(type: WidgetType): boolean {
  return type !== 'table';
}

// ─── Add/edit widget form ─────────────────────────────────────────────────────

const addWidgetFormBaseSchema = z.object({
  title: z
    .string()
    .min(1, { error: 'dashboard:addWidget.form.validation.titleRequired' })
    .max(100, {
      error: 'dashboard:addWidget.form.validation.titleTooLong',
    }),
  type: z.enum([
    'counter',
    'donut',
    'pie',
    'bar',
    'horizontal_bar',
    'stacked_bar',
    'line',
    'area',
    'table',
  ]),
  metric: z.string(),
  widthLabel: z.enum(['1/4', '1/3', '1/2', '2/3', 'Completo']),
  heightLabel: z.enum(['Compacto', 'Normal', 'Alto', 'Extra']),
  aggregationFunction: z.enum(['count', 'sum', 'avg', 'min', 'max', 'percent']).optional(),
  aggregationCondition: z.enum(['completed', 'archived', 'has_assignee']).optional(),
  // Scope — restrict the widget's data (tenant dashboard: project; either: board).
  scopeProjectId: z.string().optional(),
  scopeBoardId: z.string().optional(),
  // Query
  archivedOnly: z.boolean().optional(),
  maxItems: z.enum(['10', '20', '50', '100']).optional(),
  // Only the three fields WidgetDataService knows how to resolve.
  dateRangeField: z.enum(['createdAt', 'dueDate', 'startDate']).optional(),
  // Matches WidgetDataService.ResolvePreset exactly — no other value is
  // recognized by the backend. `custom` is a UI-only option: it is translated
  // into an absent preset + explicit start/end when building the payload.
  dateRangePreset: z.enum(['this_week', 'this_month', 'last_7', 'last_30', 'custom']).optional(),
  dateRangeStart: z.string().optional(),
  dateRangeEnd: z.string().optional(),
  filters: filtersSchema.optional(),
});

export type AddWidgetFormInput = z.infer<typeof addWidgetFormBaseSchema>;

/**
 * Builds the add/edit widget schema for a given dashboard scope. groupBy
 * availability also depends on the project pinned in the widget's own scope,
 * which is read from the submitted values rather than passed in.
 */
export function addWidgetFormSchema(scopeType: DashboardScopeType) {
  return addWidgetFormBaseSchema.superRefine((data, ctx) => {
    const hasPinnedProject = !!data.scopeProjectId;
    const metrics = getAvailableMetrics(data.type, scopeType, hasPinnedProject);

    if (metrics.length > 0 && !data.metric) {
      ctx.addIssue({
        code: 'custom',
        path: ['metric'],
        message: 'dashboard:addWidget.form.validation.metricRequired',
      });
    } else if (data.metric && !metrics.includes(data.metric)) {
      // Covers Widget.InvalidGroupByForType and Widget.CrossScopeReference —
      // e.g. groupBy=Day on a Bar widget, or groupBy=Project on a board dashboard.
      ctx.addIssue({
        code: 'custom',
        path: ['metric'],
        message: 'dashboard:addWidget.form.validation.metricInvalid',
      });
    }

    // Widget.IncompatibleAggregation — groupBy=Status + function=Percent is
    // rejected by the domain even though both are individually valid.
    if (data.metric === 'Status' && data.aggregationFunction === 'percent') {
      ctx.addIssue({
        code: 'custom',
        path: ['aggregationFunction'],
        message: 'dashboard:addWidget.form.validation.statusPercentBlocked',
      });
    }

    // A rule without a value serializes to `"value":""`, which the backend's
    // FilterEngine fails to parse as a long and silently degrades the WHOLE
    // filter to "match everything" — never let one leave the form.
    if (data.filters && hasIncompleteRule(data.filters)) {
      ctx.addIssue({
        code: 'custom',
        path: ['filters'],
        message: 'dashboard:addWidget.form.validation.filterIncomplete',
      });
    }

    if (data.dateRangePreset === 'custom' && !data.dateRangeStart && !data.dateRangeEnd) {
      ctx.addIssue({
        code: 'custom',
        path: ['dateRangeStart'],
        message: 'dashboard:addWidget.form.validation.dateRangeRequired',
      });
    }

    // The date range is only sent when a period is chosen — a lone date field
    // would be dropped silently and look like the form lost it.
    if (data.dateRangeField && !data.dateRangePreset) {
      ctx.addIssue({
        code: 'custom',
        path: ['dateRangePreset'],
        message: 'dashboard:addWidget.form.validation.dateRangePresetRequired',
      });
    }
  });
}

// ─── Filter completeness ──────────────────────────────────────────────────────

const NO_VALUE_OPERATORS = new Set(['is_empty', 'is_not_empty']);

function isFilterGroup(node: unknown): node is WidgetFilterGroup {
  return typeof node === 'object' && node !== null && 'rules' in node;
}

/** True when any leaf rule in the tree still needs a value the user hasn't picked. */
export function hasIncompleteRule(group: WidgetFilterGroup): boolean {
  return group.rules.some((node) => {
    if (isFilterGroup(node)) return hasIncompleteRule(node);
    if (NO_VALUE_OPERATORS.has(node.operator)) return false;
    if (Array.isArray(node.value)) return node.value.length === 0;
    return node.value === undefined || node.value === null || node.value === '';
  });
}

// ─── Size label mappers ───────────────────────────────────────────────────────

const WIDTH_MAP: Record<WidthLabel, 3 | 4 | 6 | 8 | 12> = {
  '1/4': 3,
  '1/3': 4,
  '1/2': 6,
  '2/3': 8,
  Completo: 12,
};

const HEIGHT_MAP: Record<HeightLabel, 2 | 3 | 4 | 6> = {
  Compacto: 2,
  Normal: 3,
  Alto: 4,
  Extra: 6,
};

export function widthLabelToCol(label: WidthLabel): 3 | 4 | 6 | 8 | 12 {
  return WIDTH_MAP[label];
}

export function heightLabelToRow(label: HeightLabel): 2 | 3 | 4 | 6 {
  return HEIGHT_MAP[label];
}

export function colToWidthLabel(col: number): WidthLabel {
  const entry = Object.entries(WIDTH_MAP).find(([, v]) => v === col);
  return (entry?.[0] as WidthLabel) ?? '1/2';
}

export function rowToHeightLabel(row: number): HeightLabel {
  const entry = Object.entries(HEIGHT_MAP).find(([, v]) => v === row);
  return (entry?.[0] as HeightLabel) ?? 'Normal';
}
